import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  Archive as ArchiveIcon,
  Trash2 as TrashIcon,
  Search,
  X,
  RefreshCw,
  Plus,
  Library,
  Loader2,
  CheckSquare,
  Square,
  FolderInput,
  RotateCcw,
  Trash,
} from 'lucide-react';
import { io } from 'socket.io-client';
import FolderSidebar from './FolderSidebar';
import MediaGrid from './MediaGrid';
import ReferenceSafetyDialog from './ReferenceSafetyDialog';
import { filterToSection } from './mediaHelpers';
import {
  archiveMedia,
  unarchiveMedia,
  trashMedia,
  restoreMedia,
  purgeMedia,
  moveMediaToFolder,
  bulkMediaAction,
  listFolders,
  createFolder,
  patchFolder,
  deleteFolder,
  listLibrary,
} from '../../api/contentApi';
import './media-v2.css';
import { CONTENT_CATEGORIES } from '../../utils/contentCategories';

// Dashboard socket — DEFAULT namespace. Backend emits `media:state` and
// `playlist:update` on "/", not on "/tv" (TV apps use /tv). Kept module-local
// singleton so repeated MediaV2Shell mounts reuse one connection.
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000';
let rootSocketSingleton = null;
const getRootSocket = () => {
  if (rootSocketSingleton) return rootSocketSingleton;
  rootSocketSingleton = io(SOCKET_URL, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: 5,
  });
  return rootSocketSingleton;
};

const TABS = [
  { value: 'active', label: 'Library', icon: Library },
  { value: 'archived', label: 'Archive', icon: ArchiveIcon },
  { value: 'trash', label: 'Trash', icon: TrashIcon },
];

const FILTER_SECTIONS = [
  { value: 'images', label: 'Images' },
  { value: 'videos', label: 'Videos' },
  { value: 'brochures', label: 'Brochures' },
];

/** Droppable tab header (Archive / Trash). Drop triggers the safety dialog. */
const TabDropZone = ({ value, active, children, onClick }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `tab:${value}`,
    data: { type: 'tab', tab: value },
    disabled: value === 'active',
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      role="tab"
      aria-selected={active}
      className={[
        'mv2-tab',
        active && 'mv2-tab--active',
        isOver && 'mv2-tab--over',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

/**
 * MediaV2Shell
 *
 * Tabs (Library / Archive / Trash) + section filter + search + folder sidebar
 * + month-grouped grid + DnD + reference-safety flow.
 *
 * Props pass-through for schedule integration with the legacy shell:
 *   allLibraryItems: Array  — pre-filtered items (type+section+state) supplied by parent
 *   scheduleByContentId: map
 *   renderCard: (item, options) => ReactNode — legacy renderMediaCard
 *   onSchedule: (item) => void
 *   onUploadClick: () => void
 *   onRefresh: () => void  — triggers parent library refetch
 *   projectId: string
 *   videoPreviewRef
 *   contentFilter, setContentFilter
 *   searchQuery, setSearchQuery
 */
const MediaV2Shell = ({
  allLibraryItems = [],
  scheduleByContentId = {},
  renderCard,
  onSchedule,
  onUploadClick,
  onRefresh,
  projectId,
  videoPreviewRef,
  contentFilter,
  setContentFilter,
  searchQuery,
  setSearchQuery,
  isLibraryLoading,
}) => {
  const [activeTab, setActiveTab] = useState('active');
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Category filter — null = "all". Filters viewItems client-side.
  const [categoryFilter, setCategoryFilter] = useState(null);
  // Bulk-assign category dropdown open-state.
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [toast, setToast] = useState(null); // { kind: 'ok'|'err', msg }
  const [refreshTick, setRefreshTick] = useState(0);

  // Safety dialog state
  const [dialogState, setDialogState] = useState(null);
  // dialogState shape: { mode: 'archive'|'trash'|'purge', items: [...] }

  // Folder delete confirm
  const [folderDeleteState, setFolderDeleteState] = useState(null);
  // { folder, cascade }

  // Move-to-folder picker
  const [movePickerState, setMovePickerState] = useState(null);
  // { items: [...] }

  const toastTimerRef = useRef(null);
  const pushToast = useCallback((kind, msg) => {
    setToast({ kind, msg });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3200);
  }, []);
  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
  }, []);

  const section = filterToSection(contentFilter);

  // Load folders on section change
  useEffect(() => {
    let cancelled = false;
    if (!projectId || !section) return undefined;
    listFolders(projectId, section)
      .then((list) => { if (!cancelled) setFolders(list || []); })
      .catch((err) => {
        console.warn('listFolders failed (non-fatal):', err?.message);
        if (!cancelled) setFolders([]);
      });
    return () => { cancelled = true; };
  }, [projectId, section, refreshTick]);

  // Reset folder selection if the folder went away (e.g. after delete)
  useEffect(() => {
    if (activeFolderId && !folders.find((f) => f._id === activeFolderId)) {
      setActiveFolderId(null);
    }
  }, [folders, activeFolderId]);

  // Archive / Trash tab items come from /library?view=... because the legacy
  // section-list endpoint filters is_deleted:{$ne:true}, so trashed items
  // never reach allLibraryItems. Active tab keeps using allLibraryItems.
  const [tabItems, setTabItems] = useState([]);
  useEffect(() => {
    if (activeTab === 'active' || !projectId || !section) {
      setTabItems([]);
      return undefined;
    }
    let cancelled = false;
    listLibrary({ projectId, section, view: activeTab })
      .then((res) => { if (!cancelled) setTabItems(res?.items || []); })
      .catch((err) => {
        console.warn(`listLibrary(${activeTab}) failed:`, err?.message);
        if (!cancelled) setTabItems([]);
      });
    return () => { cancelled = true; };
  }, [activeTab, projectId, section, refreshTick]);

  // Socket: invalidate on media:state and playlist:update.
  // Backend emits these on the DEFAULT namespace ("/"), NOT on /tv.
  // (/tv is TV-app only — see coordinator handoff for details.)
  useEffect(() => {
    const socket = getRootSocket();
    const onMediaState = (payload) => {
      console.log('[MediaV2] media:state', payload);
      setRefreshTick((t) => t + 1);
      if (onRefresh) onRefresh();
    };
    const onPlaylistUpdate = (payload) => {
      console.log('[MediaV2] playlist:update', payload);
      setRefreshTick((t) => t + 1);
    };
    socket.on('media:state', onMediaState);
    socket.on('playlist:update', onPlaylistUpdate);
    return () => {
      socket.off('media:state', onMediaState);
      socket.off('playlist:update', onPlaylistUpdate);
    };
  }, [onRefresh]);

  // Client-side filter by view + folder + search. The parent already filters
  // by type; here we take the final slice for the current tab.
  // NOTE: once backend listLibrary is live, you can swap this for a fetch —
  // but the manifest says "additive + nullable" so client filtering is safe
  // while the backend catches up. We check status / is_deleted / folder_id
  // fields defensively so old docs (no such fields) default into Library view.
  const viewItems = useMemo(() => {
    const q = searchQuery?.trim().toLowerCase() || '';
    const source = activeTab === 'active' ? allLibraryItems : tabItems;
    return source.filter((item) => {
      const status = item.status || item.raw_status || 'active';
      const isDeleted = !!(item.is_deleted || item.deleted_at);
      const isArchived = status === 'archived' || !!item.archived_at;
      if (activeTab === 'active') {
        if (isDeleted || isArchived) return false;
      } else if (activeTab === 'archived') {
        if (!isArchived || isDeleted) return false;
      } else if (activeTab === 'trash') {
        if (!isDeleted) return false;
      }
      const folderId = item.folder_id || item.folderId || null;
      if (activeFolderId) {
        if (String(folderId || '') !== String(activeFolderId)) return false;
      }
      // else root: don't filter — show ALL active-tab items regardless of folder
      // (matches "All media" semantics). Folder tabs show only their own.
      if (q) {
        const hay = [
          item.title,
          item.name,
          item.fileName,
          item.contentId,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (categoryFilter && item.category !== categoryFilter) return false;
      return true;
    });
  }, [allLibraryItems, tabItems, activeTab, activeFolderId, searchQuery, categoryFilter]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Clear selection when switching tabs / folders / sections
  useEffect(() => { clearSelection(); }, [activeTab, activeFolderId, contentFilter, clearSelection]);

  const handleToggleSelect = useCallback((itemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  }, []);

  const handleSelectMonth = useCallback((ids, selectAll) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selectAll) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const selectedItemList = useMemo(() => {
    const list = [];
    allLibraryItems.forEach((it) => {
      if (selectedIds.has(it.id)) list.push(it);
    });
    return list;
  }, [allLibraryItems, selectedIds]);

  // ─── Actions ────────────────────────────────────────────────────────────
  const withRefresh = useCallback(async (fn, successMsg, errMsg) => {
    try {
      const res = await fn();
      if (successMsg) pushToast('ok', successMsg);
      setRefreshTick((t) => t + 1);
      if (onRefresh) onRefresh();
      return res;
    } catch (err) {
      console.error(errMsg, err);
      pushToast('err', err?.message || errMsg);
      throw err;
    }
  }, [onRefresh, pushToast]);

  const cascadeSummary = (res) => {
    const c = res?.cascaded || {};
    const parts = [];
    if (c.schedule_slots) parts.push(`${c.schedule_slots} schedule${c.schedule_slots === 1 ? '' : 's'}`);
    if (c.playlist_items) parts.push(`${c.playlist_items} playlist${c.playlist_items === 1 ? '' : 's'}`);
    return parts.length ? `Detached from ${parts.join(', ')}.` : '';
  };

  // Archive = cold storage: hides the item from Library but keeps all
  // existing schedules + playlists intact. No safety dialog needed.
  const doArchive = (items) => withRefresh(
    async () => {
      if (items.length === 1) {
        const res = await archiveMedia(projectId, resolveContentId(items[0]));
        pushToast('ok', `Archived "${items[0].title || items[0].name}" — already-scheduled plays continue.`);
        return res;
      }
      const res = await bulkMediaAction(projectId, 'archive', items.map(resolveContentId).filter(Boolean));
      pushToast('ok', `Archived ${items.length} items — already-scheduled plays continue.`);
      return res;
    },
    null,
    'Failed to archive',
  ).then(() => { clearSelection(); })
    .catch(() => { /* toast already surfaced by withRefresh */ });

  // Archive/Trash tabs hydrate from /library which returns raw Mongo docs
  // keyed as content_id (snake). Active tab hydrates through a projection
  // that exposes contentId (camel). Read both so every destructive action
  // works regardless of which tab the item was surfaced on.
  const resolveContentId = (item) => item?.contentId || item?.content_id || item?.id || null;

  // Close the dialog optimistically BEFORE awaiting the network call. The
  // previous shape awaited trashMedia first and only then set dialog state
  // to null — if axios ever hung (no default timeout configured), the
  // "Working…" button stayed stuck indefinitely because the parent's
  // promise chain never ran its .then. Network errors still reach the
  // user via the toast that withRefresh pushes from its catch branch.
  const doTrash = (items) => {
    setDialogState(null);
    clearSelection();
    return withRefresh(
      async () => {
        if (items.length === 1) {
          const res = await trashMedia(projectId, resolveContentId(items[0]));
          const tail = cascadeSummary(res);
          pushToast('ok', `Moved "${items[0].title || items[0].name}" to Trash. ${tail}`.trim());
          return res;
        }
        const res = await bulkMediaAction(projectId, 'trash', items.map(resolveContentId).filter(Boolean));
        pushToast('ok', `Moved ${items.length} items to Trash.`);
        return res;
      },
      null,
      'Failed to move to Trash',
    ).catch(() => { /* toast already surfaced by withRefresh */ });
  };

  const doPurge = (items) => {
    setDialogState(null);
    clearSelection();
    return withRefresh(
      async () => {
        if (items.length === 1) {
          await purgeMedia(projectId, resolveContentId(items[0]));
          pushToast('ok', `Permanently deleted "${items[0].title || items[0].name}".`);
          return;
        }
        await bulkMediaAction(projectId, 'purge', items.map(resolveContentId).filter(Boolean));
        pushToast('ok', `Permanently deleted ${items.length} items.`);
      },
      null,
      'Failed to delete',
    ).catch(() => { /* toast already surfaced by withRefresh */ });
  };

  const doUnarchive = (item) => withRefresh(
    () => unarchiveMedia(projectId, resolveContentId(item)),
    `Unarchived "${item.title || item.name}".`,
    'Failed to unarchive',
  );

  const doRestore = (item) => withRefresh(
    () => restoreMedia(projectId, resolveContentId(item)),
    `Restored "${item.title || item.name}".`,
    'Failed to restore',
  );

  // Bulk-assign category. Marketing user backfill primary use case + ongoing
  // re-categorization. Pass null to clear.
  const doSetCategory = (items, categoryKey) => withRefresh(
    async () => {
      const ids = items.map(resolveContentId).filter(Boolean);
      await bulkMediaAction(projectId, 'set-category', ids, categoryKey);
      const label = categoryKey
        ? (CONTENT_CATEGORIES.find((c) => c.key === categoryKey)?.label || categoryKey)
        : null;
      pushToast('ok', label
        ? `Categorized ${ids.length} item${ids.length === 1 ? '' : 's'} as ${label}.`
        : `Cleared category on ${ids.length} item${ids.length === 1 ? '' : 's'}.`);
    },
    null,
    'Failed to set category',
  ).then(() => { clearSelection(); setBulkCategoryOpen(false); })
    .catch(() => { setBulkCategoryOpen(false); });

  const doMove = (items, folderId) => withRefresh(
    async () => {
      if (items.length === 1) {
        await moveMediaToFolder(projectId, items[0].contentId || items[0].id, folderId);
      } else {
        await bulkMediaAction(projectId, 'move', items.map((i) => i.contentId || i.id), folderId);
      }
      const target = folderId
        ? (folders.find((f) => f._id === folderId)?.name || 'folder')
        : 'root';
      pushToast('ok', `Moved ${items.length} item${items.length === 1 ? '' : 's'} to ${target}.`);
    },
    null,
    'Failed to move',
  ).then(() => { clearSelection(); setMovePickerState(null); });

  const handleMenuAction = (action, item, arg) => {
    // Archive is cold storage (no cascade) — skip the safety dialog.
    if (action === 'archive') doArchive([item]);
    else if (action === 'trash') setDialogState({ mode: 'trash', items: [item] });
    else if (action === 'purge') setDialogState({ mode: 'purge', items: [item] });
    else if (action === 'unarchive') doUnarchive(item);
    else if (action === 'restore') doRestore(item);
    else if (action === 'move') setMovePickerState({ items: [item] });
    else if (action === 'setCategory') doSetCategory([item], arg);
  };

  // ─── DnD ────────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeData = active?.data?.current;
    const overData = over?.data?.current;
    if (!activeData || activeData.type !== 'media') return;
    const draggedItem = activeData.item;
    // If the dragged card is part of a multi-select, move the whole selection.
    const dragIds = selectedIds.has(draggedItem.id) && selectedIds.size > 1
      ? Array.from(selectedIds).map((id) => allLibraryItems.find((it) => it.id === id)).filter(Boolean)
      : [draggedItem];

    if (overData?.type === 'folder') {
      doMove(dragIds, overData.folderId ?? null);
    } else if (overData?.type === 'tab') {
      if (overData.tab === 'archived') {
        // Archive is cold storage — drop straight through, no safety dialog.
        doArchive(dragIds);
      } else if (overData.tab === 'trash') {
        setDialogState({ mode: 'trash', items: dragIds });
      }
    }
  };

  // ─── Folder operations ──────────────────────────────────────────────────
  const handleCreateFolder = (name) => {
    createFolder(projectId, section, { name })
      .then((folder) => {
        setFolders((prev) => [...prev, folder].filter(Boolean));
        pushToast('ok', `Created folder "${name}".`);
      })
      .catch((err) => pushToast('err', err.message || 'Failed to create folder'));
  };

  const handleRenameFolder = (folderId, name) => {
    patchFolder(folderId, { name })
      .then((folder) => {
        setFolders((prev) => prev.map((f) => (f._id === folderId ? { ...f, ...folder } : f)));
        pushToast('ok', 'Folder renamed.');
      })
      .catch((err) => pushToast('err', err.message || 'Failed to rename folder'));
  };

  const handleDeleteFolder = (folder) => setFolderDeleteState({ folder, cascade: 'detach' });

  const confirmDeleteFolder = () => {
    const { folder, cascade } = folderDeleteState || {};
    if (!folder) return;
    deleteFolder(folder._id, cascade)
      .then(() => {
        setFolders((prev) => prev.filter((f) => f._id !== folder._id));
        if (activeFolderId === folder._id) setActiveFolderId(null);
        pushToast('ok', `Folder "${folder.name}" deleted (${cascade}).`);
        setFolderDeleteState(null);
        setRefreshTick((t) => t + 1);
        if (onRefresh) onRefresh();
      })
      .catch((err) => {
        pushToast('err', err.message || 'Failed to delete folder');
        setFolderDeleteState(null);
      });
  };

  const resultCount = viewItems.length;
  const selectedCount = selectedIds.size;

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="mv2-shell">
        {/* Tabs */}
        <div className="mv2-tabs" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabDropZone
                key={t.value}
                value={t.value}
                active={activeTab === t.value}
                onClick={() => setActiveTab(t.value)}
              >
                <Icon size={14} />
                <span>{t.label}</span>
              </TabDropZone>
            );
          })}
        </div>

        {/* Section filter + search + actions */}
        <div className="mv2-toolbar">
          <div className="mv2-toolbar__left">
            <div className="media__section-filter">
              {FILTER_SECTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`media__section-btn${contentFilter === o.value ? ' media__section-btn--active' : ''}`}
                  onClick={() => setContentFilter(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="media__search">
              <span className="media__search-icon"><Search size={16} /></span>
              <input
                type="text"
                className="media__search-input"
                placeholder="Search assets…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="media__search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              aria-label="Filter by category"
              className="media__category-filter"
              value={categoryFilter || ''}
              onChange={(e) => setCategoryFilter(e.target.value || null)}
              style={{
                padding: '6px 10px',
                fontSize: 13,
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              <option value="">All categories</option>
              {CONTENT_CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="mv2-toolbar__right">
            <span className="media__result-count">
              <strong>{resultCount}</strong>
              {resultCount === 1 ? ' asset' : ' assets'}
            </span>
            <button
              type="button"
              className="mv2-btn mv2-btn--ghost"
              onClick={() => { setRefreshTick((t) => t + 1); if (onRefresh) onRefresh(); }}
              aria-label="Refresh"
              disabled={isLibraryLoading}
            >
              {isLibraryLoading ? <Loader2 size={15} className="mv2-spin" /> : <RefreshCw size={15} />}
            </button>
            {activeTab === 'active' && onUploadClick && (
              <button
                type="button"
                className="mv2-btn mv2-btn--primary"
                onClick={onUploadClick}
              >
                <Plus size={14} /> Upload
              </button>
            )}
          </div>
        </div>

        {/* Bulk action toolbar */}
        {selectedCount > 0 && (
          <div className="mv2-bulkbar" role="toolbar" aria-label="Bulk actions">
            <div className="mv2-bulkbar__count">
              <CheckSquare size={14} /> {selectedCount} selected
            </div>
            <div className="mv2-bulkbar__actions">
              {activeTab === 'active' && (
                <>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      type="button"
                      className="mv2-btn mv2-btn--ghost"
                      onClick={() => setBulkCategoryOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={bulkCategoryOpen}
                    >
                      Set category…
                    </button>
                    {bulkCategoryOpen && (
                      <div
                        role="menu"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                          zIndex: 20,
                          minWidth: 180,
                          padding: 4,
                        }}
                        onMouseLeave={() => setBulkCategoryOpen(false)}
                      >
                        {CONTENT_CATEGORIES.map((c) => (
                          <button
                            key={c.key}
                            type="button"
                            role="menuitem"
                            onClick={() => doSetCategory(selectedItemList, c.key)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              padding: '8px 10px',
                              background: 'none',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: 13,
                              color: '#111827',
                            }}
                          >
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: c.color }} />
                            {c.label}
                          </button>
                        ))}
                        <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => doSetCategory(selectedItemList, null)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px 10px',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: 13,
                            color: '#6b7280',
                          }}
                        >
                          Clear category
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--ghost"
                    onClick={() => setMovePickerState({ items: selectedItemList })}
                  >
                    <FolderInput size={14} /> Move…
                  </button>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--ghost"
                    onClick={() => doArchive(selectedItemList)}
                  >
                    <ArchiveIcon size={14} /> Archive
                  </button>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--danger"
                    onClick={() => setDialogState({ mode: 'trash', items: selectedItemList })}
                  >
                    <TrashIcon size={14} /> Trash
                  </button>
                </>
              )}
              {activeTab === 'archived' && (
                <>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--ghost"
                    onClick={() => selectedItemList.forEach(doUnarchive)}
                  >
                    <RotateCcw size={14} /> Unarchive
                  </button>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--danger"
                    onClick={() => setDialogState({ mode: 'trash', items: selectedItemList })}
                  >
                    <TrashIcon size={14} /> Trash
                  </button>
                </>
              )}
              {activeTab === 'trash' && (
                <>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--ghost"
                    onClick={() => selectedItemList.forEach(doRestore)}
                  >
                    <RotateCcw size={14} /> Restore
                  </button>
                  <button
                    type="button"
                    className="mv2-btn mv2-btn--danger"
                    onClick={() => setDialogState({ mode: 'purge', items: selectedItemList })}
                  >
                    <Trash size={14} /> Delete Forever
                  </button>
                </>
              )}
              <button
                type="button"
                className="mv2-btn mv2-btn--ghost"
                onClick={clearSelection}
              >
                <Square size={14} /> Clear
              </button>
            </div>
          </div>
        )}

        {/* Body: sidebar + grid */}
        <div className="mv2-body">
          {activeTab === 'active' && (
            <FolderSidebar
              folders={folders}
              activeFolderId={activeFolderId}
              onSelect={setActiveFolderId}
              onCreate={handleCreateFolder}
              onRename={handleRenameFolder}
              onDelete={handleDeleteFolder}
            />
          )}
          <div className="mv2-main">
            {isLibraryLoading ? (
              <div className="media__loading">Loading content…</div>
            ) : (
              <MediaGrid
                items={viewItems}
                view={activeTab}
                renderCard={renderCard}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onSelectMonth={handleSelectMonth}
                onSchedule={onSchedule}
                onMenuAction={handleMenuAction}
                hoveredCard={hoveredCard}
                setHoveredCard={setHoveredCard}
                videoPreviewRef={videoPreviewRef}
                scheduleByContentId={scheduleByContentId}
              />
            )}
          </div>
        </div>

        {toast && (
          <div
            role="status"
            aria-live="polite"
            className={`mv2-toast mv2-toast--${toast.kind}`}
          >
            {toast.msg}
          </div>
        )}

        {/* Safety dialog */}
        <ReferenceSafetyDialog
          open={!!dialogState}
          mode={dialogState?.mode || 'archive'}
          items={dialogState?.items || []}
          projectId={projectId}
          onCancel={() => setDialogState(null)}
          onConfirm={(items) => {
            if (dialogState?.mode === 'archive') return doArchive(items);
            if (dialogState?.mode === 'trash') return doTrash(items);
            if (dialogState?.mode === 'purge') return doPurge(items);
            return undefined;
          }}
        />

        {/* Folder delete dialog */}
        {folderDeleteState && (
          <div className="mv2-modal-overlay" onClick={() => setFolderDeleteState(null)}>
            <div className="mv2-modal-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className="mv2-modal-title">Delete folder?</h3>
              <p className="mv2-modal-body">
                &quot;{folderDeleteState.folder.name}&quot; — choose what happens to its media:
              </p>
              <div className="mv2-modal-choices">
                <label>
                  <input
                    type="radio"
                    name="cascade"
                    value="detach"
                    checked={folderDeleteState.cascade === 'detach'}
                    onChange={() => setFolderDeleteState((s) => ({ ...s, cascade: 'detach' }))}
                  />
                  <span><strong>Detach</strong> — move media to root (keep it).</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="cascade"
                    value="trash"
                    checked={folderDeleteState.cascade === 'trash'}
                    onChange={() => setFolderDeleteState((s) => ({ ...s, cascade: 'trash' }))}
                  />
                  <span><strong>Trash</strong> — send all media in this folder to Trash.</span>
                </label>
              </div>
              <div className="mv2-modal-actions">
                <button type="button" className="mv2-btn mv2-btn--ghost" onClick={() => setFolderDeleteState(null)}>
                  Cancel
                </button>
                <button type="button" className="mv2-btn mv2-btn--danger" onClick={confirmDeleteFolder}>
                  Delete folder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move-to-folder picker */}
        {movePickerState && (
          <div className="mv2-modal-overlay" onClick={() => setMovePickerState(null)}>
            <div className="mv2-modal-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className="mv2-modal-title">Move to folder</h3>
              <p className="mv2-modal-body">
                Choose a destination for {movePickerState.items.length} item{movePickerState.items.length === 1 ? '' : 's'}.
              </p>
              <div className="mv2-modal-folder-list">
                <button
                  type="button"
                  className="mv2-modal-folder-btn"
                  onClick={() => doMove(movePickerState.items, null)}
                >
                  Root (All media)
                </button>
                {folders.map((f) => (
                  <button
                    key={f._id}
                    type="button"
                    className="mv2-modal-folder-btn"
                    onClick={() => doMove(movePickerState.items, f._id)}
                  >
                    {f.name}
                  </button>
                ))}
                {folders.length === 0 && (
                  <p className="mv2-modal-body" style={{ marginTop: 8 }}>
                    No folders in this section. Create one from the sidebar.
                  </p>
                )}
              </div>
              <div className="mv2-modal-actions">
                <button type="button" className="mv2-btn mv2-btn--ghost" onClick={() => setMovePickerState(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
};

export default MediaV2Shell;
